import type { Spot } from "./types.js";
/**
 * Court geometry in real metres, from FIVB 2025-2028 Rule 1 and Rule 2.
 *
 * Coordinate system, used by every module in the domain:
 *
 *   origin  the centre of the court at floor level, directly under the net
 *   +x      toward the right sideline as our team faces the net
 *   +z      away from the net, into our half (0 m at the net, 9 m at our end line)
 *   +y      up
 *
 * The far side of the net is therefore negative z, which is what
 * `mirrorToFarSide` produces.
 */

export const COURT = {
  width: 9, // Rule 1.1: 18 x 9 m, so each half is 9 x 9 m
  halfDepth: 9,
  halfWidth: 4.5,
  attackLine: 3, // Rule 1.3.4: rear edge 3 m back from the centre line
  serviceZoneWidth: 9, // Rule 1.4.2: 9 m wide behind the end line
  freeZone: 5, // Rule 1.1: 5 m sideline free zone for official competition
  lineWidth: 0.05, // Rule 1.3.1: lines are 5 cm wide
};

export const NET = {
  heights: { women: 2.24, men: 2.43 }, // Rule 2.1.1
  depth: 1, // Rule 2.2: 1 m deep
  length: 9.5, // Rule 2.2: 9.50 to 10 m long
  mesh: 0.1, // Rule 2.2: 10 cm square black mesh
  topBand: 0.07, // Rule 2.3: 7 cm white canvas band
  bottomBand: 0.05, // Rule 2.3: 5 cm band along the bottom
  antennaHeight: 0.8, // Rule 2.4: antenna protrudes 80 cm above the net
};

export const ZONES = [1, 2, 3, 4, 5, 6];

/**
 * The legal resting spot at the middle of each zone.
 *
 * Zones are 3 m x 3 m thirds of our half, so the anchors sit at the centre of
 * each third: 1.5 m from the net for the front row, 4.5 m for the back row.
 * Deliberately not the geometric centre in depth for the back row — 6 m would
 * crowd the end line and read badly on screen, and any depth beyond the attack
 * line is equally legal.
 */
const ANCHORS: Record<number, Spot> = {
  4: { x: -3, z: 1.6 },
  3: { x: 0, z: 1.6 },
  2: { x: 3, z: 1.6 },
  5: { x: -3, z: 6.2 },
  6: { x: 0, z: 6.2 },
  1: { x: 3, z: 6.2 },
};

/** The centre-of-zone spot for a zone number, in court metres. */
export function zoneAnchor(zone: number) {
  const anchor = ANCHORS[zone];
  if (!anchor) throw new RangeError(`no such zone: ${zone}`);
  return { ...anchor };
}

/** True for a depth inside the front zone (between the net and the attack line). */
export const inFrontZone = (z: number) => Math.abs(z) < COURT.attackLine;

/** Rotate a spot 180 degrees about the net centre, onto the opponents' half. */
export const mirrorToFarSide = ({ x, z }: Spot) => ({ x: -x, z: -z });
